import { useEffect, useState } from 'react';

import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Card, Col, Input, Row, Select, Tag, Typography } from 'antd';

import Button from '../../../custom/button/button';
import { getPaymentChannels, requeryPayment } from './request';
import userStyles from '../../userManagement/styles.module.scss';

const { Title, Text } = Typography;

const STATUS_COLOR: Record<string, string> = {
  successful: 'success',
  failed: 'error',
  pending: 'warning',
};

const formatLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

const formatValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const InfoItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Col xs={24} sm={12} lg={8} className={userStyles.infoItem}>
    <Text className={userStyles.infoLabel}>{label}</Text>
    <div className={userStyles.infoValue}>{value || 'N/A'}</div>
  </Col>
);

const PaymentRequery = () => {
  const { notification } = App.useApp();

  const [paymentReference, setPaymentReference] = useState('');
  const [paymentChannel, setPaymentChannel] = useState<string | undefined>(undefined);

  const { data: channels = [], isLoading: isLoadingChannels } = useQuery({
    queryKey: ['payment-channels'],
    queryFn: getPaymentChannels,
  });

  useEffect(() => {
    if (!paymentChannel && channels.length > 0) {
      setPaymentChannel(channels[0].name.toLowerCase());
    }
  }, [channels, paymentChannel]);

  const {
    mutate: requery,
    data: result,
    isPending,
    reset,
  } = useMutation({
    mutationFn: requeryPayment,
    onError: (error: any) => {
      notification.error({
        message: 'Requery Failed',
        description:
          error?.response?.data?.message || 'Unable to requery this payment. Please try again.',
      });
    },
  });

  const handleRequery = () => {
    const trimmedReference = paymentReference.trim();
    if (!trimmedReference || !paymentChannel) return;
    requery({ paymentReference: trimmedReference, paymentChannel });
  };

  const handleReferenceChange = (value: string) => {
    setPaymentReference(value);
    if (result) reset();
  };

  const isSuccessful = result?.transactionStatus?.toLowerCase() === 'successful';

  return (
    <Card>
      <Title level={4} style={{ marginBottom: 20 }}>
        Payment Requery
      </Title>

      <Row gutter={12} style={{ marginBottom: 24 }}>
        <Col>
          <Input
            style={{ width: 320 }}
            placeholder="Enter payment reference"
            prefix={<SearchOutlined />}
            value={paymentReference}
            onChange={(e) => handleReferenceChange(e.target.value)}
            onPressEnter={handleRequery}
            allowClear
          />
        </Col>
        <Col>
          <Select
            style={{ width: 200 }}
            placeholder="Payment channel"
            loading={isLoadingChannels}
            value={paymentChannel}
            onChange={(value) => setPaymentChannel(value)}
            options={channels.map((channel) => ({
              value: channel.name.toLowerCase(),
              label: channel.name,
            }))}
          />
        </Col>
        <Col>
          <Button
            text="Requery Payment"
            iconBefore={<ReloadOutlined />}
            isLoading={isPending}
            disabled={!paymentReference.trim() || !paymentChannel}
            onClick={handleRequery}
            type="button"
          />
        </Col>
      </Row>

      {result && (
        <div className={userStyles.verifyForm}>
          <Text strong>Requery Result</Text>
          <Row gutter={[16, 12]} className={userStyles.infoGrid} style={{ marginTop: 12 }}>
            {Object.entries(result).map(([key, value]) => (
              <InfoItem
                key={key}
                label={formatLabel(key)}
                value={
                  key === 'transactionStatus' ? (
                    <Tag color={STATUS_COLOR[String(value).toLowerCase()] ?? 'default'}>
                      {String(value)}
                    </Tag>
                  ) : (
                    formatValue(value)
                  )
                }
              />
            ))}
          </Row>
          {!isSuccessful && (
            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              This payment is not yet successful. You may requery again once the transaction has
              been completed on the payment channel.
            </Text>
          )}
        </div>
      )}
    </Card>
  );
};

export default PaymentRequery;
